// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import React, { useContext } from "react";

import { useFeatureVariants } from "../../../../components/StoreProvider";
import type { UsTxArsErsBlockingSubmitFieldKey } from "../../../../WorkflowsStore/Opportunity/UsTx/UsTxArsErsSharedUtils";
import { FormViewerContext } from "../../FormViewer";
import FormCheckbox from "../../shared/FormCheckbox";
import FormHeader from "../../shared/FormHeader";
import FormInput from "../../shared/FormInput";
import { Footer, FormContainer, Grid, Item, Row } from "../../shared/styles";
import { PrintablePage } from "../../styles";
import { useArsErsSectionsDisabled } from "../utils";

const FormPSV323D: React.FC<{
  sectionEditedByCurrentUser?: UsTxArsErsBlockingSubmitFieldKey;
}> = ({ sectionEditedByCurrentUser }) => {
  const formViewerContext = useContext(FormViewerContext);
  const featureVariants = useFeatureVariants();
  const {
    paroleOfficerSectionDisabled,
    unitSupervisorSectionDisabled,
    paroleSupervisorSectionDisabled,
    assistantRegionDirectorSectionDisabled,
    regionDirectorSectionDisabled,
    markSectionEditedLocally,
  } = useArsErsSectionsDisabled(sectionEditedByCurrentUser, featureVariants);

  return (
    <PrintablePage>
      <FormContainer {...formViewerContext}>
        <Item style={{ padding: "unset" }}>
          <FormHeader
            titleLineOne="Annual Reporting"
            titleLineTwo="Checklist, Recommendation, and Determination Form"
            subTitle="Clients who meet the following criteria may be recommended for annual reporting."
          />
          <Row paddingLeft>
            <Item>
              Client's Name:
              <FormInput
                name="clientName"
                placeholder="Enter First, Last Name"
                style={{ width: 412 }}
              />
            </Item>
          </Row>
          <Row paddingLeft justifyContentStart>
            <Item>
              TDCJ/SID #:
              <FormInput name="tdcjNumberAndSid" style={{ width: 175 }} />
            </Item>
            <Item>
              Eligibility Month:
              <FormInput name="eligibilityMonthString" style={{ width: 175 }} />
            </Item>
          </Row>
        </Item>
        <Row justifyContentStart>
          <Item width={25}>Y</Item>
          <Item width={25}>N</Item>
          <Item width={508}>
            1. The client has satisfactorily completed three years on Low
            supervision by the Texas Risk Assessment System (TRAS).
          </Item>
        </Row>
        <Row justifyContentStart>
          <Item width={25}>
            <FormCheckbox
              toggleable
              name="threeYearsTRASCheck"
              id="threeYearsTRASCheckYes"
            ></FormCheckbox>
          </Item>
          <Item width={25}>
            <FormCheckbox
              toggleable
              invert
              name="threeYearsTRASCheck"
              id="threeYearsTRASCheckNo"
            ></FormCheckbox>
          </Item>
          <Item>Comments:</Item>
        </Row>
        <Row paddingLeft>
          <Item>
            <FormInput
              name="comment1"
              style={{ width: 500, borderBottom: "solid thin" }}
            />
          </Item>
        </Row>
        <Row justifyContentStart>
          <Item width={25}>Y</Item>
          <Item width={25}>N</Item>
          <Item width={508}>
            2. The client has demonstrated a good faith effort to comply with
            supervision, crime victim fees and Post Secondary Education
            reimbursement required as of release, which will continue to be due
            monthly, unless paid in advance.
          </Item>
        </Row>
        <Row justifyContentStart>
          <Item width={25}>
            <FormCheckbox
              toggleable
              name="complianceFeesAndEducationCheck"
              id="complianceFeesAndEducationCheckYes"
            ></FormCheckbox>
          </Item>
          <Item width={25}>
            <FormCheckbox
              toggleable
              invert
              name="complianceFeesAndEducationCheck"
              id="complianceFeesAndEducationCheckNo"
            ></FormCheckbox>
          </Item>
          <Item>Comments:</Item>
        </Row>
        <Row paddingLeft>
          <Item>
            <FormInput
              name="comment2"
              style={{ width: 500, borderBottom: "solid thin" }}
            />
          </Item>
        </Row>
        <Row justifyContentStart>
          <Item width={25}>Y</Item>
          <Item width={25}>N</Item>
          <Item width={508}>
            3. The client has maintained compliance with all restitution
            obligations in accordance to PD/POP-3.1.6 for the preceding two
            years of supervision, which will continue to be due each month until
            fully paid. **The client's restitution obligations do not have to be
            paid in full to qualify.**
          </Item>
        </Row>
        <Row justifyContentStart>
          <Item width={25}>
            <FormCheckbox
              toggleable
              name="restitutionObligationsCheck"
              id="restitutionObligationsCheckYes"
            ></FormCheckbox>
          </Item>
          <Item width={25}>
            <FormCheckbox
              toggleable
              invert
              name="restitutionObligationsCheck"
              id="restitutionObligationsCheckNo"
            ></FormCheckbox>
          </Item>
          <Item>Comments:</Item>
        </Row>
        <Row paddingLeft>
          <Item>
            <FormInput
              name="comment3"
              style={{ width: 500, borderBottom: "solid thin" }}
            />
          </Item>
        </Row>
        <Row justifyContentStart>
          <Item width={25}>Y</Item>
          <Item width={25}>N</Item>
          <Item width={508}>
            4. The client has not had a warrant issued with in the preceding two
            years of supervision. This does not apply to a warrant issued in
            which a subsequent investigation or administrative review did not
            sustain the violation.
          </Item>
        </Row>
        <Row justifyContentStart>
          <Item width={25}>
            <FormCheckbox
              toggleable
              name="warrantCheck"
              id="warrantCheckYes"
            ></FormCheckbox>
          </Item>
          <Item width={25}>
            <FormCheckbox
              toggleable
              invert
              name="warrantCheck"
              id="warrantCheckNo"
            ></FormCheckbox>
          </Item>
          <Item>Comments:</Item>
        </Row>
        <Row paddingLeft>
          <Item>
            <FormInput
              name="comment4"
              style={{ width: 500, borderBottom: "solid thin" }}
            />
          </Item>
        </Row>
        <Row justifyContentStart>
          <Item width={25}>Y</Item>
          <Item width={25}>N</Item>
          <Item width={508}>
            5. It is in the best interest of society, per the supervising
            officer's discretion for the client's reporting status to be
            modified to Annual Report.
          </Item>
        </Row>
        <Row justifyContentStart>
          <Item width={25}>
            <FormCheckbox
              toggleable
              name="societyBestInterestCheck"
              id="societyBestInterestCheckYes"
            ></FormCheckbox>
          </Item>
          <Item width={25}>
            <FormCheckbox
              toggleable
              invert
              name="societyBestInterestCheck"
              id="societyBestInterestCheckNo"
            ></FormCheckbox>
          </Item>
          <Item>Comments:</Item>
        </Row>
        <Row paddingLeft>
          <Item>
            <FormInput
              name="comment5"
              style={{ width: 500, borderBottom: "solid thin" }}
            />
          </Item>
        </Row>
        <Row style={{ fontSize: 8, fontWeight: "bold" }}>
          This form should be forwarded through the supervising officer's entire
          chain of command, regardless of the recommendation.
        </Row>
        <Row style={{ fontSize: 8, fontWeight: "bold" }}>
          The Region Director will make the final decision regarding the
          client's Annual Report eligibility
        </Row>
        <Grid columns="2.6fr 1.8fr 2.9fr">
          <Item>
            Supervising Officer:{" "}
            <FormInput
              name="officerName"
              style={{
                borderRight: "unset",
                fontSize: "8px",
                marginLeft: "5px",
              }}
              disabled={paroleOfficerSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally("paroleOfficer", "officerName", value)
              }
            />
          </Item>
          <Item>
            Date:
            <FormInput
              name="supervisingOfficerDate"
              style={{ borderRight: "unset" }}
              disabled={paroleOfficerSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "paroleOfficer",
                  "supervisingOfficerDate",
                  value,
                )
              }
            />
          </Item>
          <Item textAlignCenter>
            Supervising Officer Recommend Client for Annual Report
          </Item>
        </Grid>
        <Grid columns="4.44fr 1.45fr 1.45fr">
          <Item>
            Signature confirms that all checklist information has been
            thoroughly reviewed.
          </Item>
          <Item textAlignCenter>Yes</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="supervisingOfficerRecommendCheckYes"
              id="supervisingOfficerRecommendCheckYes"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="supervisingOfficerRecommendCheckNo"
              disabled={paroleOfficerSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "paroleOfficer",
                  "supervisingOfficerRecommendCheckYes",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.75fr 3.67fr 1.45fr 1.45fr">
          <Item>Signature:</Item>
          <FormInput
            name="supervisingOfficerSignature"
            disabled={paroleOfficerSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "paroleOfficer",
                "supervisingOfficerSignature",
                value,
              )
            }
          />
          <Item textAlignCenter>No</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="supervisingOfficerRecommendCheckNo"
              id="supervisingOfficerRecommendCheckNo"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="supervisingOfficerRecommendCheckYes"
              disabled={paroleOfficerSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "paroleOfficer",
                  "supervisingOfficerRecommendCheckNo",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.745fr 6.5fr" style={{ borderBottom: "1px solid" }}>
          <Item>Remarks:</Item>
          <FormInput
            name="supervisingOfficerRemarks"
            disabled={paroleOfficerSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "paroleOfficer",
                "supervisingOfficerRemarks",
                value,
              )
            }
          />
        </Grid>
        <Item
          style={{ lineHeight: "unset", height: "3px", display: "flex" }}
        ></Item>
        <Grid columns="2.6fr 1.8fr 2.9fr">
          <Item>
            Unit Supervisor:{" "}
            <FormInput
              name="unitSupervisorName"
              style={{ borderRight: "unset" }}
              disabled={unitSupervisorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "unitSupervisor",
                  "unitSupervisorName",
                  value,
                )
              }
            />
          </Item>
          <Item>
            Date:
            <FormInput
              name="unitSupervisorDate"
              style={{ borderRight: "unset" }}
              disabled={unitSupervisorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "unitSupervisor",
                  "unitSupervisorDate",
                  value,
                )
              }
            />
          </Item>
          <Item textAlignCenter>
            Concur with Supervising Officer's Decision
          </Item>
        </Grid>
        <Grid columns="4.44fr 1.45fr 1.45fr">
          <Item>
            Signature confirms that all checklist information has been
            thoroughly reviewed.
          </Item>
          <Item textAlignCenter>Yes</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="unitSupervisorConcurWithSupervisingOfficerCheckYes"
              id="unitSupervisorConcurWithSupervisingOfficerCheckYes"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="unitSupervisorConcurWithSupervisingOfficerCheckNo"
              disabled={unitSupervisorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "unitSupervisor",
                  "unitSupervisorConcurWithSupervisingOfficerCheckYes",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.75fr 3.67fr 1.45fr 1.45fr">
          <Item>Signature:</Item>
          <FormInput
            name="unitSupervisorSignature"
            disabled={unitSupervisorSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "unitSupervisor",
                "unitSupervisorSignature",
                value,
              )
            }
          />
          <Item textAlignCenter>No</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="unitSupervisorConcurWithSupervisingOfficerCheckNo"
              id="unitSupervisorConcurWithSupervisingOfficerCheckNo"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="unitSupervisorConcurWithSupervisingOfficerCheckYes"
              disabled={unitSupervisorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "unitSupervisor",
                  "unitSupervisorConcurWithSupervisingOfficerCheckNo",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.745fr 6.5fr" style={{ borderBottom: "1px solid" }}>
          <Item>Remarks:</Item>
          <FormInput
            name="unitSupervisorRemarks"
            disabled={unitSupervisorSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "unitSupervisor",
                "unitSupervisorRemarks",
                value,
              )
            }
          />
        </Grid>
        <Item
          style={{ lineHeight: "unset", height: "3px", display: "flex" }}
        ></Item>
        <Grid columns="2.6fr 1.8fr 2.9fr">
          <Item>
            Parole Supervisor:{" "}
            <FormInput
              name="paroleSupervisorName"
              style={{ borderRight: "unset" }}
              disabled={paroleSupervisorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "paroleSupervisor",
                  "paroleSupervisorName",
                  value,
                )
              }
            />
          </Item>
          <Item>
            Date:
            <FormInput
              name="paroleSupervisorDate"
              style={{ borderRight: "unset" }}
              disabled={paroleSupervisorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "paroleSupervisor",
                  "paroleSupervisorDate",
                  value,
                )
              }
            />
          </Item>
          <Item textAlignCenter>
            Concur with Supervising Officer's Decision
          </Item>
        </Grid>
        <Grid columns="4.44fr 1.45fr 1.45fr">
          <Item>
            Signature confirms that all checklist information has been
            thoroughly reviewed.
          </Item>
          <Item textAlignCenter>Yes</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="paroleSupervisorConcurWithSupervisingOfficerCheckYes"
              id="paroleSupervisorConcurWithSupervisingOfficerCheckYes"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="paroleSupervisorConcurWithSupervisingOfficerCheckNo"
              disabled={paroleSupervisorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "paroleSupervisor",
                  "paroleSupervisorConcurWithSupervisingOfficerCheckYes",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.75fr 3.67fr 1.45fr 1.45fr">
          <Item>Signature:</Item>
          <FormInput
            name="paroleSupervisorSignature"
            disabled={paroleSupervisorSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "paroleSupervisor",
                "paroleSupervisorSignature",
                value,
              )
            }
          />
          <Item textAlignCenter>No</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="paroleSupervisorConcurWithSupervisingOfficerCheckNo"
              id="paroleSupervisorConcurWithSupervisingOfficerCheckNo"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="paroleSupervisorConcurWithSupervisingOfficerCheckYes"
              disabled={paroleSupervisorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "paroleSupervisor",
                  "paroleSupervisorConcurWithSupervisingOfficerCheckNo",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.745fr 6.5fr" style={{ borderBottom: "1px solid" }}>
          <Item>Remarks:</Item>
          <FormInput
            name="paroleSupervisorRemarks"
            disabled={paroleSupervisorSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "paroleSupervisor",
                "paroleSupervisorRemarks",
                value,
              )
            }
          />
        </Grid>
        <Item
          style={{ lineHeight: "unset", height: "3px", display: "flex" }}
        ></Item>
        <Grid columns="2.6fr 1.8fr 2.9fr">
          <Item>
            Assistant Region Director:{" "}
            <FormInput
              name="assistantRegionDirectorName"
              style={{ borderRight: "unset" }}
              disabled={assistantRegionDirectorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "assistantRegionDirector",
                  "assistantRegionDirectorName",
                  value,
                )
              }
            />
          </Item>
          <Item>
            Date:
            <FormInput
              name="assistantRegionDirectorDate"
              style={{ borderRight: "unset" }}
              disabled={assistantRegionDirectorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "assistantRegionDirector",
                  "assistantRegionDirectorDate",
                  value,
                )
              }
            />
          </Item>
          <Item textAlignCenter>
            Concur with Supervising Officer's Decision
          </Item>
        </Grid>
        <Grid columns="4.44fr 1.45fr 1.45fr">
          <Item>
            Signature confirms that all checklist information has been
            thoroughly reviewed.
          </Item>
          <Item textAlignCenter>Yes</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="assistantRegionDirectorConcurWithSupervisingOfficerCheckYes"
              id="assistantRegionDirectorConcurWithSupervisingOfficerCheckYes"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="assistantRegionDirectorConcurWithSupervisingOfficerCheckNo"
              disabled={assistantRegionDirectorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "assistantRegionDirector",
                  "assistantRegionDirectorConcurWithSupervisingOfficerCheckYes",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.75fr 3.67fr 1.45fr 1.45fr">
          <Item>Signature:</Item>
          <FormInput
            name="assistantRegionDirectorSignature"
            disabled={assistantRegionDirectorSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "assistantRegionDirector",
                "assistantRegionDirectorSignature",
                value,
              )
            }
          />
          <Item textAlignCenter>No</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="assistantRegionDirectorConcurWithSupervisingOfficerCheckNo"
              id="assistantRegionDirectorConcurWithSupervisingOfficerCheckNo"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="assistantRegionDirectorConcurWithSupervisingOfficerCheckYes"
              disabled={assistantRegionDirectorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "assistantRegionDirector",
                  "assistantRegionDirectorConcurWithSupervisingOfficerCheckNo",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.745fr 6.5fr" style={{ borderBottom: "1px solid" }}>
          <Item>Remarks:</Item>
          <FormInput
            name="assistantRegionDirectorRemarks"
            disabled={assistantRegionDirectorSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "assistantRegionDirector",
                "assistantRegionDirectorRemarks",
                value,
              )
            }
          />
        </Grid>
        <Item
          style={{ lineHeight: "unset", height: "3px", display: "flex" }}
        ></Item>
        <Grid columns="2.6fr 1.8fr 2.9fr">
          <Item>
            Region Director:{" "}
            <FormInput
              name="regionDirectorName"
              style={{ borderRight: "unset" }}
              disabled={regionDirectorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "regionDirector",
                  "regionDirectorName",
                  value,
                )
              }
            />
          </Item>
          <Item>
            Date:
            <FormInput
              name="regionDirectorDate"
              style={{ borderRight: "unset" }}
              disabled={regionDirectorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "regionDirector",
                  "regionDirectorDate",
                  value,
                )
              }
            />
          </Item>
          <Item textAlignCenter>Final Authority</Item>
        </Grid>
        <Grid columns="4.44fr 1.45fr 1.45fr">
          <Item>
            Signature confirms that all checklist information has been
            thoroughly reviewed.
          </Item>
          <Item textAlignCenter>Yes</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="regionDirectorConcurWithSupervisingOfficerCheckYes"
              id="regionDirectorConcurWithSupervisingOfficerCheckYes"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="regionDirectorConcurWithSupervisingOfficerCheckNo"
              disabled={regionDirectorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "regionDirector",
                  "regionDirectorConcurWithSupervisingOfficerCheckYes",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.75fr 3.67fr 1.45fr 1.45fr">
          <Item>Signature:</Item>
          <FormInput
            name="regionDirectorSignature"
            disabled={regionDirectorSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "regionDirector",
                "regionDirectorSignature",
                value,
              )
            }
          />
          <Item textAlignCenter>No</Item>
          <Item textAlignCenter>
            <FormCheckbox
              toggleable
              name="regionDirectorConcurWithSupervisingOfficerCheckNo"
              id="regionDirectorConcurWithSupervisingOfficerCheckNo"
              style={{ height: "10px", verticalAlign: "middle" }}
              manualInvert="regionDirectorConcurWithSupervisingOfficerCheckYes"
              disabled={regionDirectorSectionDisabled}
              onValueChange={(value) =>
                markSectionEditedLocally(
                  "regionDirector",
                  "regionDirectorConcurWithSupervisingOfficerCheckNo",
                  value,
                )
              }
            ></FormCheckbox>
          </Item>
        </Grid>
        <Grid columns="0.745fr 6.5fr" style={{ borderBottom: "1px solid" }}>
          <Item>Remarks:</Item>
          <FormInput
            name="regionDirectorRemarks"
            disabled={regionDirectorSectionDisabled}
            onValueChange={(value) =>
              markSectionEditedLocally(
                "regionDirector",
                "regionDirectorRemarks",
                value,
              )
            }
          />
        </Grid>

        <Footer>PSV-323D (Rev. 02/10/2026)</Footer>
      </FormContainer>
    </PrintablePage>
  );
};

export default FormPSV323D;
